import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTelegram } from '../context/TelegramContext';
import { useCurrency } from '../context/CurrencyContext';
import styled from 'styled-components';
import Layout from '../components/layout/Layout';
import MonthSelector from '../components/common/MonthSelector';
import StatisticsTabs from '../components/statistics/StatisticsTabs';
import SharedDataToggle from '../components/statistics/SharedDataToggle';
import CategoryChart from '../components/statistics/CategoryChart';
import ChartComponent from '../components/statistics/ChartComponent';
import TransactionsTable from '../components/statistics/TransactionsTable';
import { fetchExchangeRates, FALLBACK_RATES } from '../utils/currencyUtils';

const Container = styled.div`
  padding: 16px;
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #4a6fa1;
  margin: 0;
  
  @media (max-width: 576px) {
    font-size: 20px;
  }
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  background: linear-gradient(145deg, #f0f3f9, #e6ebf5);
  color: #4a6fa1;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  font-size: 14px;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.05),
              -2px -2px 5px rgba(255, 255, 255, 0.8);
  transition: all 0.2s ease;
  
  &:hover, &:focus {
    background: linear-gradient(145deg, #e6ebf5, #dde2ec);
    transform: translateY(-2px);
    box-shadow: 3px 3px 7px rgba(0, 0, 0, 0.07),
                -3px -3px 7px rgba(255, 255, 255, 0.9);
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: inset 1px 1px 3px rgba(0, 0, 0, 0.1),
                inset -1px -1px 3px rgba(255, 255, 255, 0.9);
  }
`;

const ChartToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
`;

const ChartToggleButton = styled.button`
  background: ${props => props.$isActive 
    ? 'linear-gradient(145deg, #4a6fa1, #5d82b3)' 
    : 'linear-gradient(145deg, #f0f3f9, #e6ebf5)'};
  color: ${props => props.$isActive ? 'white' : '#4a6fa1'};
  border: none;
  padding: 10px 16px;
  border-radius: ${props => props.$position === 'left' ? '8px 0 0 8px' : '0 8px 8px 0'};
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${props => props.$isActive 
    ? '2px 2px 5px rgba(0, 0, 0, 0.1), -2px -2px 5px rgba(255, 255, 255, 0.1)' 
    : '2px 2px 5px rgba(0, 0, 0, 0.05), -2px -2px 5px rgba(255, 255, 255, 0.8)'};
  
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    margin-right: 6px;
    width: 16px;
    height: 16px;
  }
  
  &:hover {
    transform: ${props => props.$isActive ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.$isActive 
      ? '2px 2px 5px rgba(0, 0, 0, 0.1), -2px -2px 5px rgba(255, 255, 255, 0.1)' 
      : '3px 3px 7px rgba(0, 0, 0, 0.07), -3px -3px 7px rgba(255, 255, 255, 0.9)'};
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// Иконка круговой диаграммы
const PieChartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M11 2v20c-5.07-.5-9-4.79-9-10s3.93-9.5 9-10zm2.03 0v8.99H22c-.47-4.74-4.24-8.52-8.97-8.99zm0 11.01V22c4.74-.47 8.5-4.25 8.97-8.99h-8.97z"/>
  </svg>
);

// Иконка гистограммы
const BarChartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M4 9h4v11H4zm6-5h4v16h-4zm6 8h4v8h-4z"/>
  </svg>
);

// Определение символов валют
const CURRENCY_SYMBOLS = {
  'USD': '$',
  'EUR': '€',
  'UAH': '₴',
  'RUB': '₽',
  'GBP': '£',
  'JPY': '¥',
  'CNY': '¥',
  'INR': '₹',
  'CAD': 'C$',
  'AUD': 'A$',
  'BRL': 'R$',
  'CHF': 'Fr',
  'PLN': 'zł',
  'TRY': '₺'
};

// Функция форматирования валюты
const formatCurrency = (amount, currencyCode = 'USD') => {
  if (amount === null || amount === undefined) {
    return '0.00';
  }
  
  const numAmount = Number(amount);
  if (isNaN(numAmount)) {
    return '0.00';
  }
  
  // Форматирование с нужным разделителем в зависимости от локали
  const formattedNumber = new Intl.NumberFormat(undefined, {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numAmount);
  
  // Получаем символ валюты
  const currencySymbol = CURRENCY_SYMBOLS[currencyCode] || currencyCode;
  
  // Для некоторых валют символ ставится после числа
  if (['UAH', 'RUB', 'PLN'].includes(currencyCode)) {
    return `${formattedNumber} ${currencySymbol}`;
  } else {
    return `${currencySymbol}${formattedNumber}`;
  }
};

const Statistics = () => {
  const { t } = useTranslation();
  const { telegramUserId } = useTelegram();
  const { userCurrency } = useCurrency();
  
  // Определяем текущую валюту
  const [currentCurrency, setCurrentCurrency] = useState(() => {
    const storedCurrency = localStorage.getItem('preferred_currency') || localStorage.getItem('user_currency');
    return storedCurrency || userCurrency || 'USD';
  });
  
  // Состояния компонента
  const [currentMonth, setCurrentMonth] = useState(null);
  const [currentType, setCurrentType] = useState('expenses');
  const [includeSharedData, setIncludeSharedData] = useState(() => {
  const storedValue = localStorage.getItem('includeSharedData');
  // Если значение уже есть в localStorage, используем его
  // По умолчанию показываем общие данные (true)
  return storedValue !== null ? JSON.parse(storedValue) : true;
  });
  const [transactions, setTransactions] = useState([]);
  const [hasLinkedAccounts, setHasLinkedAccounts] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [exchangeRates, setExchangeRates] = useState(FALLBACK_RATES);
  const [chartType, setChartType] = useState('pie');
  const [updateKey, setUpdateKey] = useState(Date.now());
  
  // Отслеживаем изменения валюты из контекста
  useEffect(() => {
    if (userCurrency && userCurrency !== currentCurrency) {
      setCurrentCurrency(userCurrency);
      setUpdateKey(Date.now());
    }
  }, [userCurrency, currentCurrency]);
  
  // Отслеживаем изменения валюты из localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if ((e.key === 'preferred_currency' || e.key === 'user_currency') && e.newValue) {
        if (e.newValue !== currentCurrency) {
          setCurrentCurrency(e.newValue);
          setUpdateKey(Date.now());
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [currentCurrency]);
  
  // Обработчик события изменения валюты
  useEffect(() => {
    const handleCurrencyChange = () => {
      const storedCurrency = localStorage.getItem('preferred_currency') || localStorage.getItem('user_currency');
      if (storedCurrency && storedCurrency !== currentCurrency) {
        setCurrentCurrency(storedCurrency);
        setUpdateKey(Date.now());
      }
    };
    
    // Подписка на пользовательское событие изменения валюты
    window.addEventListener('currencyChanged', handleCurrencyChange);
    
    // Отписка при размонтировании
    return () => {
      window.removeEventListener('currencyChanged', handleCurrencyChange);
    };
  }, [currentCurrency]);

  // Загрузка текущего месяца
  useEffect(() => {
    const loadCurrentMonth = async () => {
      const savedMonth = localStorage.getItem('selected_month');
      
      if (savedMonth && parseInt(savedMonth) >= 1 && parseInt(savedMonth) <= 12) {
        setCurrentMonth(parseInt(savedMonth));
        return;
      }
      
      try {
        const response = await fetch('https://fftg.farodinprojects.org:3000/currentmonth');
        const data = await response.json();
        if (data && data.current_month && data.current_month >= 1 && data.current_month <= 12) {
          setCurrentMonth(data.current_month);
          localStorage.setItem('selected_month', data.current_month.toString());
        } else {
          const now = new Date();
          const currentMonth = now.getMonth() + 1;
          setCurrentMonth(currentMonth);
          localStorage.setItem('selected_month', currentMonth.toString());
        }
      } catch (error) {
        console.error('Error loading current month:', error);
        const now = new Date();
        const currentMonth = now.getMonth() + 1;
        setCurrentMonth(currentMonth);
        localStorage.setItem('selected_month', currentMonth.toString());
      }
    };

    loadCurrentMonth();
  }, []);

  // Загрузка курсов валют
  useEffect(() => {
    const loadExchangeRates = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const response = await fetch('https://fftg.farodinprojects.org:3000/exchange_rates', {
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`Error fetching exchange rates: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        if (!data.rates || Object.keys(data.rates).length === 0) {
          setExchangeRates(FALLBACK_RATES);
        } else {
          setExchangeRates(data.rates);
        }
      } catch (error) {
        console.error('[Statistics] Error fetching exchange rates:', error);
        setExchangeRates(FALLBACK_RATES);
      }
    };

    loadExchangeRates();
  }, []);

  // Проверка связанных аккаунтов
  useEffect(() => {
    const checkLinkedAccounts = async () => {
      if (!telegramUserId) return;

      try {
        const response = await fetch(
          `https://fftg.farodinprojects.org:3000/get_linked_accounts?user_id=${telegramUserId}`
        );
        const accounts = await response.json();
        setHasLinkedAccounts(Array.isArray(accounts) && accounts.length > 0);
      } catch (error) {
        console.error('[Statistics] Error checking linked accounts:', error);
        setHasLinkedAccounts(false);
      }
    };

    checkLinkedAccounts();
  }, [telegramUserId]);

  // Загрузка транзакций
 const loadTransactions = useCallback(async () => {
  if (!telegramUserId || !currentMonth) return;

  setIsLoading(true);
  try {
    console.log(`[Statistics] Loading transactions with shared=${includeSharedData ? 'true' : 'false'}`); // Правильный лог
    
    const response = await fetch(
      `https://fftg.farodinprojects.org:3000/transactions?user_id=${telegramUserId}&month=${currentMonth}&type=${currentType}&shared=${includeSharedData ? 'true' : 'false'}`
    );

    if (!response.ok) {
      throw new Error(`Error loading transactions: ${response.statusText}`);
    }

    const data = await response.json();
    console.log(`[Statistics] Loaded ${data.length} transactions`);
    setTransactions(data);
  } catch (error) {
    console.error('[Statistics] Error loading transactions:', error);
    setTransactions([]);
  } finally {
    setIsLoading(false);
  }
}, [telegramUserId, currentMonth, currentType, includeSharedData]);
  // Вызов загрузки транзакций при изменении зависимостей
  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  // Обработчик удаления транзакции
  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm(t('transaction_delete_confirm'))) return;

    try {
      const response = await fetch('https://fftg.farodinprojects.org:3000/delete_transaction', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transaction_id: Number(transactionId),
          type: currentType
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        const updatedTransactions = transactions.filter(t => t.id !== transactionId);
        setTransactions(updatedTransactions);
        alert(t('transaction_delete_success'));
      } else {
        alert(`Error: ${data.error || t('transaction_delete_failed')}`);
      }
    } catch (error) {
      console.error('[Statistics] Error deleting transaction:', error);
      alert(t('transaction_delete_failed'));
    }
  };

  const handleMonthChange = (month) => {
    setCurrentMonth(month);
    localStorage.setItem('selected_month', month.toString());
  };

  const handleTypeChange = (type) => {
    setCurrentType(type);
  };
// Найдите функцию handleToggleSharedData и замените ее на следующую:

const handleToggleSharedData = (e) => {
  const newValue = e.target.checked;
  console.log('[Statistics] Toggling shared data:', newValue);
  
  // Установка состояния
  setIncludeSharedData(newValue);
  
  // Сохранение в localStorage
  localStorage.setItem('includeSharedData', newValue);
  
  // Перезагрузка данных с новым значением
  loadTransactions();
};
  
  const handleChartTypeChange = (type) => {
    setChartType(type);
  };

  return (
    <Layout>
      <Container>
        <PageHeader>
          <PageTitle>{t('stats_page_title')}</PageTitle>
          <BackButton to="/">
            {t('back_button')}
          </BackButton>
        </PageHeader>

        <MonthSelector 
          currentMonth={currentMonth} 
          onMonthChange={handleMonthChange} 
        />

        <StatisticsTabs 
          currentType={currentType} 
          onTypeChange={handleTypeChange} 
        />

        {hasLinkedAccounts && (
          <SharedDataToggle 
            includeSharedData={includeSharedData} 
            onToggle={handleToggleSharedData} 
          />
        )}
        
        <ChartToggleContainer>
          <ChartToggleButton 
            $isActive={chartType === 'pie'} 
            $position="left"
            onClick={() => handleChartTypeChange('pie')}
          >
            <PieChartIcon />
            {t('pie_chart_option')}
          </ChartToggleButton>
          <ChartToggleButton 
            $isActive={chartType === 'histogram'} 
            $position="right"
            onClick={() => handleChartTypeChange('histogram')}
          >
            <BarChartIcon />
            {t('histogram_option')}
          </ChartToggleButton>
        </ChartToggleContainer>

        {chartType === 'pie' ? (
          <CategoryChart 
            key={`pie-${currentCurrency}-${updateKey}`}
            transactions={transactions} 
            exchangeRates={exchangeRates}
            preferredCurrency={currentCurrency}
            isLoading={isLoading}
            enhancedView={true}
            formatCurrency={formatCurrency}
          />
        ) : (
          <ChartComponent 
            key={`chart-${currentCurrency}-${updateKey}`}
            transactions={transactions}
            exchangeRates={exchangeRates}
            preferredCurrency={currentCurrency}
            isLoading={isLoading}
            month={currentMonth}
            type={currentType}
            formatCurrency={formatCurrency}
          />
        )}

        <TransactionsTable
          key={`table-${currentCurrency}-${updateKey}`} 
          transactions={transactions}
          exchangeRates={exchangeRates}
          preferredCurrency={currentCurrency}
          onDelete={handleDeleteTransaction}
          isLoading={isLoading}
          formatCurrency={formatCurrency}
        />
      </Container>
    </Layout>
  );
};

export default Statistics;